/*
  Speakeasy cart drawer (<cart-drawer>, markup in sections/cart-drawer.liquid).

  Implements the same API surface product-form.js expects of <cart-notification>:
    setActiveElement(el), renderContents(parsedState), getSectionsToRender().

  Quantity/remove updates POST JSON to routes.cart_change_url with the
  cart.js three-field sections pattern (id = DOM id, section = SRA filename,
  selector = swapped node). Do NOT load cart.js on non-cart pages — it crashes
  without #main-cart-items. Depends on global.js (debounce, fetchConfig,
  trapFocus, removeTrapFocus, <quantity-input>), which loads first.

  Events:
    'speakeasy:cart-drawer:open'    -> open()
    'speakeasy:cart-drawer:refresh' -> re-fetch + swap sections (never auto-opens)
*/
if (!customElements.get('cart-drawer')) {
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLElement {
      constructor() {
        super();

        this.panel = this.querySelector('.spk-cart-drawer__panel');
        this.overlay = this.querySelector('.spk-cart-drawer__overlay');
        this.statusElement = this.querySelector('#CartDrawer-LineItemStatus');
        this.activeElement = null;
        this.currentItemCount = this.readItemCount();

        this.addEventListener('keyup', (event) => {
          if (event.code === 'Escape') this.close();
        });

        if (this.overlay) this.overlay.addEventListener('click', () => this.close());

        const closeButton = this.querySelector('.spk-cart-drawer__close');
        if (closeButton) closeButton.addEventListener('click', () => this.close());

        // Open from the header bag icon without touching header markup
        // (#cart-icon-bubble markup is AJAX-swapped, so intercept at document level).
        document.addEventListener(
          'click',
          (event) => {
            const bubble = event.target instanceof Element ? event.target.closest('#cart-icon-bubble') : null;
            if (!bubble) return;
            event.preventDefault();
            this.open(bubble);
          },
          { capture: true }
        );

        // Quantity changes bubble up from <quantity-input> and the number inputs.
        this.debouncedOnChange = debounce((event) => this.onChange(event), 300);
        this.addEventListener('change', (event) => {
          if (event.target && event.target.name === 'updates[]') this.debouncedOnChange(event);
        });

        // Remove buttons are re-rendered on every swap — delegate.
        this.addEventListener('click', (event) => {
          const removeButton =
            event.target instanceof Element ? event.target.closest('.spk-cart-drawer__remove') : null;
          if (!removeButton) return;
          event.preventDefault();
          this.updateQuantity(removeButton.dataset.index, 0);
        });

        document.addEventListener('speakeasy:cart-drawer:open', () => this.open());
        document.addEventListener('speakeasy:cart-drawer:refresh', () => this.refresh());
      }

      setActiveElement(element) {
        this.activeElement = element;
      }

      open(openedBy) {
        if (openedBy) this.setActiveElement(openedBy);
        else if (!this.activeElement) this.setActiveElement(document.activeElement);

        if (this.hasAttribute('open')) {
          // Already open (e.g. add-to-cart while browsing the open drawer):
          // contents may have been swapped, so re-engage the trap on fresh nodes.
          if (this.panel) trapFocus(this.panel);
          return;
        }

        this.classList.add('active');
        this.setAttribute('open', '');
        document.body.classList.add('overflow-hidden');

        if (!this.panel) return;
        let trapped = false;
        const engageTrap = () => {
          if (trapped || !this.hasAttribute('open')) return;
          trapped = true;
          trapFocus(this.panel);
        };
        this.panel.addEventListener('transitionend', engageTrap, { once: true });
        setTimeout(engageTrap, 400); // fallback in case no transition fires
      }

      close() {
        if (!this.hasAttribute('open')) return;
        this.classList.remove('active');
        this.removeAttribute('open');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.activeElement);
        this.activeElement = null;
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-drawer-items',
            section: 'cart-drawer-items',
            selector: '.shopify-section',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return element ? element.innerHTML : null;
      }

      renderSections(sections) {
        if (!sections) return;
        this.getSectionsToRender().forEach((section) => {
          const target = document.getElementById(section.id);
          if (!target || typeof sections[section.section] !== 'string') return;
          const html = this.getSectionInnerHTML(sections[section.section], section.selector);
          if (html !== null) target.innerHTML = html;
        });
        this.currentItemCount = this.readItemCount();
      }

      // Called by product-form.js after a successful /cart/add
      // (which requested our sections by id — same strings as `section`).
      renderContents(parsedState) {
        this.renderSections(parsedState.sections);
        this.open();
      }

      onChange(event) {
        const input = event.target;
        if (!input || !input.dataset || !input.dataset.index) return;
        const activeName =
          document.activeElement && document.activeElement.getAttribute
            ? document.activeElement.getAttribute('name')
            : null;
        this.updateQuantity(input.dataset.index, input.value, activeName);
      }

      updateQuantity(line, quantity, name) {
        if (!line) return;
        this.enableLoading();
        this.hideErrors();

        const body = JSON.stringify({
          line,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);
            const previousItemCount = this.currentItemCount;

            this.renderSections(parsedState.sections);

            if (parsedState.errors || parsedState.status) {
              // /cart/change rejected the request outright.
              this.showLineError(
                line,
                parsedState.description || parsedState.message || window.cartStrings.error,
                false
              );
            } else if (
              typeof parsedState.item_count === 'number' &&
              parsedState.item_count === previousItemCount
            ) {
              // Same rule as Dawn's cart.js: an unchanged item count after a
              // change request means the quantity was capped (e.g. inventory).
              const quantityInput = document.getElementById(`CartDrawer-Quantity-${line}`);
              this.showLineError(
                line,
                window.cartStrings.quantityError.replace(
                  '[quantity]',
                  quantityInput ? quantityInput.value : quantity
                ),
                true
              );
            }

            // Contents were swapped: restore focus and re-engage the trap.
            if (this.hasAttribute('open') && this.panel) {
              const lineItem = document.getElementById(`CartDrawer-Item-${line}`);
              const focusTarget = lineItem && name ? lineItem.querySelector(`[name="${name}"]`) : null;
              trapFocus(this.panel, focusTarget || this.panel);
            }
          })
          .catch((error) => {
            console.error(error);
            this.showGlobalError(
              (window.cartStrings && window.cartStrings.error) || 'There was an error while updating your cart.'
            );
          })
          .finally(() => {
            this.disableLoading();
          });
      }

      refresh() {
        const rootUrl = this.dataset.rootUrl || '/';
        const sectionNames = this.getSectionsToRender()
          .map((section) => section.section)
          .join(',');

        fetch(`${rootUrl}?sections=${sectionNames}`)
          .then((response) => response.json())
          .then((sections) => {
            this.renderSections(sections);
            if (this.hasAttribute('open') && this.panel) trapFocus(this.panel);
          })
          .catch((error) => console.error(error));
      }

      readItemCount() {
        return Array.from(this.querySelectorAll('[name="updates[]"]')).reduce(
          (total, input) => total + (parseInt(input.value, 10) || 0),
          0
        );
      }

      showLineError(line, message, isTrustedHtml) {
        const sink = document.getElementById(`CartDrawer-LineError-${line}`);
        if (!sink) {
          this.showGlobalError(message);
          return;
        }
        // cartStrings.quantityError is a trusted HTML theme translation;
        // anything else (server messages) is inserted as text.
        if (isTrustedHtml) sink.innerHTML = message;
        else sink.textContent = message;
        sink.removeAttribute('hidden');
      }

      showGlobalError(message) {
        const sink = this.querySelector('#CartDrawer-Errors');
        if (sink) {
          sink.textContent = message;
          sink.removeAttribute('hidden');
        } else if (window.Speakeasy && typeof window.Speakeasy.toast === 'function') {
          window.Speakeasy.toast(message);
        }
      }

      hideErrors() {
        this.querySelectorAll('.spk-cart-drawer__error').forEach((sink) => {
          sink.setAttribute('hidden', '');
          sink.textContent = '';
        });
      }

      enableLoading() {
        this.classList.add('spk-loading');
        const contents = this.querySelector('#cart-drawer-items');
        if (contents) contents.setAttribute('aria-busy', 'true');
        if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'false');
        if (document.activeElement && this.contains(document.activeElement)) document.activeElement.blur();
      }

      disableLoading() {
        this.classList.remove('spk-loading');
        const contents = this.querySelector('#cart-drawer-items');
        if (contents) contents.removeAttribute('aria-busy');
        if (this.statusElement) this.statusElement.setAttribute('aria-hidden', 'true');
      }
    }
  );
}
